import React, { memo, MouseEventHandler, useState } from "react";

import styled from "styled-components";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";

import { classIf } from "utility/style";

import SidebarContent from "./Content";

// -----------------------------------------------------------------------------

const Sidebar = memo(function Sidebar({
  mode = "full",
  onClose,
}: SidebarProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);

  //
  const Icon = collapsed
    ? KeyboardDoubleArrowRightIcon
    : KeyboardDoubleArrowLeftIcon;

  //
  function toggleCollapsed() {
    setCollapsed(toggleBoolean);
  }

  function closeMobileMenu(e: React.MouseEvent) {
    if ((e.target as HTMLDivElement).closest("a") !== null) {
      onClose?.();
    } else {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  //
  return mode === "full" ? (
    <ScrollContainer className={classIf(collapsed, "iconOnly")}>
      <Container>
        <SidebarContent iconOnly={collapsed} />
        <Spacer />
        <CollapseButton onClick={toggleCollapsed}>
          <Icon />
          {collapsed ? null : <Box component="span">Collapse</Box>}
        </CollapseButton>
      </Container>
    </ScrollContainer>
  ) : (
    <MobileContainer onClick={onClose as MouseEventHandler<HTMLDivElement>}>
      <MobileCloseButton onClick={onClose as MouseEventHandler<HTMLDivElement>}>
        Close
      </MobileCloseButton>
      <MobileContent
        onClick={closeMobileMenu as React.MouseEventHandler<HTMLDivElement>}
      >
        <SidebarContent />
      </MobileContent>
    </MobileContainer>
  );
});

export default Sidebar;

// -----------------------------------------------------------------------------

function toggleBoolean(value: boolean): boolean {
  return !value;
}

// -----------------------------------------------------------------------------

const CollapseButton = styled(Button)`
  background: #fff;
  border-radius: 0;
  bottom: 0;
  color: #000;
  left: 0;
  padding: 0;
  min-width: auto;
  z-index: 1000;
  height: 48px;
  width: 100%;

  span {
    font-size: 16px;
    margin-left: 8px;
    text-transform: capitalize;
  }

  svg {
    color: rgba(0, 0, 0, 0.54);
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    color: #000;
  }
`;

const ScrollContainer = styled.div`
  position: relative;
  min-height: 0;
  min-width: 192px;
  width: clamp(192px, calc(12rem + ((1vw - 8px) * 27)), 300px);

  &.iconOnly {
    width: 58px;
    min-width: 58px;
  }

  @media (max-width: 800px) {
    display: none;

    ${CollapseButton} {
      display: none;
    }
  }
`;

const Container = styled.div`
  background: #fff;
  border-right: 1px rgba(0, 0, 0, 0.1) solid;
  bottom: 0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  left: 0;
  overflow-x: hidden;
  overflow-y: auto;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
`;

const Spacer = styled.div`
  flex-grow: 1;
`;

const MobileCloseButton = styled.div`
  background: #666;
  color: #ccc;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  text-align: center;
  padding: 8px;
  z-index: 1000;
`;

const MobileContainer = styled.div`
  background: rgba(0, 0, 0, 0.1);
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 999;
`;

const MobileContent = styled.div`
  background: #fff;
  flex-grow: 1;
  width: 100%;
`;

// -----------------------------------------------------------------------------

interface SidebarProps {
  mode?: "full" | "mobile";
  onClose?: Function;
  collapsed?: boolean;
}
